import { TypedDocumentNode, gql } from '@apollo/client';
import { faGift } from '@fortawesome/pro-solid-svg-icons';
import { isBefore } from 'date-fns';
import { useState } from 'react';

import { FootballManagerTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import { useTickerContext } from '@sorare/core/src/contexts/ticker';
import { randomizedPollInterval } from '@sorare/core/src/gql';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';

import { DailyClaimButtonOnboarding } from '../DailyClaimButtonOnboarding';
import { DailyClaimDialog } from '../DailyClaimDialog';
import {
  DailyClaimButtonQuery,
  DailyClaimButtonQueryVariables,
} from './__generated__/index.graphql';

const query = gql`
  query DailyClaimButtonQuery {
    currentUser {
      slug
      nbaUserProfile {
        id
        dailyClaimTasks {
          id
          aasmState
          readyAt
        }
      }
    }
  }
` as TypedDocumentNode<DailyClaimButtonQuery, DailyClaimButtonQueryVariables>;

const POLL_INTERVAL = randomizedPollInterval(5);

export const DailyClaimButton = () => {
  const {
    flags: { useRailsToPowerNba = false },
  } = useFeatureFlags();

  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const { now } = useTickerContext();

  const { data } = useQuery(query, {
    pollInterval: POLL_INTERVAL,
  });

  const tasks = data?.currentUser?.nbaUserProfile?.dailyClaimTasks || [];
  const hasClaimTasks = tasks.length > 0;

  if (!useRailsToPowerNba || !hasClaimTasks) {
    return null;
  }
  const hasClaimTaskReady = tasks.some(
    t =>
      [
        FootballManagerTaskState.READY,
        FootballManagerTaskState.COMPLETED,
      ].includes(t.aasmState) &&
      t.readyAt &&
      isBefore(t.readyAt, now)
  );

  return (
    <>
      <DailyClaimButtonOnboarding
        setClaimDialogOpen={setClaimDialogOpen}
        hasClaimTaskReady={hasClaimTaskReady}
      />
      <OnboardingHighlightWrapper highlightName="DailyClaimButton">
        <IconButton
          color={hasClaimTaskReady ? 'primary' : 'opa'}
          icon={faGift}
          onClick={() => setClaimDialogOpen(true)}
        />
        <DailyClaimDialog
          open={claimDialogOpen}
          onClose={() => setClaimDialogOpen(false)}
        />
      </OnboardingHighlightWrapper>
    </>
  );
};
